import React, {useState, useEffect} from 'react';
import WorkingSpace from "../WorkingSpace/WorkingSpace";
import Ozobot, {
    ClearOzobotStorage,
    GetStorage,
    RunPuzzleCommand
} from "./Ozobot/Ozobot";

export default function Simulator(props) {
    const puzzleSize = 157;
    const directions = {
        's': 0,
        'sw': 45,
        'w': 90,
        'wn': 135,
        'n': 180,
        'ne': 225,
        'e': 270,
        'es': 315,
    };

    useEffect(() => {
        if (props.resetSimulator) {
            handleSimulatorEnd();
            props.setResetSimulator(false);
        }
    });

    const [isSimulatorStarted, setIsSimulatorStarted] = useState(false);

    const [ozobotDirection, setOzobotDirection] = useState('s');
    const [ozobotColor, setOzobotColor] = useState(null);
    const [ozobotMargin, setOzobotMargin] = useState(0);
    const [ozobotLeftRightMargin, setOzobotLeftRightMargin] = useState(0);

    const [isOzobotDragging, setIsOzobotDragging] = useState(false);
    const [currentPuzzleState, setCurrentPuzzleState] = useState(JSON.stringify({
        x: 0,
        y: 0,
        direction: 'n',
        id: 'ozobot',
    }));
    const getCurrentPuzzle = () => JSON.parse(localStorage.getItem('currentPuzzle'));
    const setCurrentPuzzle = c => localStorage.setItem('currentPuzzle', JSON.stringify(c));

    const [isOzobotStopped, setIsOzobotStopped] = useState(false);

    useEffect(() =>setCurrentPuzzle({
            x: 0,
            y: 0,
            direction: 'n',
            id: 'ozobot',
        }), []);

    const [scale, setScale] = useState(1);
    const handleScaleChange = s => setScale(s);

    const [diffCounter, setDiffCounter] = useState(0);

    const [clientDragX, setClientDragX] = useState(null);
    const [clientDragY, setClientDragY] = useState(null);

    const [canvas, setCanvas] = useState(null);
    const [canvasContainer, setCanvasContainer] = useState(null);
    const getCanvas = () => {
        if (!canvas) {
            setCanvas(document.querySelector('#drawing'));
            setCanvasContainer(document.querySelector('#workingSpaceContainer'));
        } else return canvas;
    };

    const renderOzobot = () => {
        let currentPuzzle = JSON.parse(currentPuzzleState);

        return currentPuzzle && <Ozobot
            top={currentPuzzle.y * puzzleSize}
            left={currentPuzzle.x * puzzleSize}
            margin={ozobotMargin}
            marginX={ozobotLeftRightMargin}
            angle={directions[ozobotDirection]}
            scale={scale}
            id="ozobot"
            onDragStart={() => setIsOzobotDragging(true)}
            onDragEnd={() => setIsOzobotDragging(false)}
            color={ozobotColor}
        />;
    };

    const onOzobotOverPuzzleOver = e => {
        if (isOzobotDragging) {
            const puzzle = {
                x: parseFloat(e.currentTarget.getAttribute('data-x')),
                y: parseFloat(e.currentTarget.getAttribute('data-y')),
                id: e.currentTarget.getAttribute('data-id'),
                direction: e.currentTarget.getAttribute('data-direction'),
            };
            setCurrentPuzzle(puzzle);
            setCurrentPuzzleState(JSON.stringify(puzzle));
        }
    };

    const handleDrawingDragOver = e => {
        e.preventDefault();
        setClientDragX(e.clientX);
        setClientDragY(e.clientY);
    };

    const getPuzzleByPosition = (x, y) => {
        const puzzles = JSON.parse(localStorage.getItem('puzzlesState'));
        const filtered = puzzles.puzzlesOnBoard.present.filter(p => p.x === x && p.y === y);

        return filtered.length > 0 ? filtered[0] : null;
    };

    const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
    const findNextPuzzle = (puzzle, direction) => {
        let x = puzzle.x;
        let y = puzzle.y;
        switch (direction) {
            case 'n':
                y -= 1;
                break;
            case 'e':
                x += 1;
                break;
            case 's':
                y += 1;
                break;
            case 'w':
                x -= 1;
                break;
            default:
                return null;
        }

        return getPuzzleByPosition(x, y);
    };

    const moveToTarget = async (currentPuzzle, nextPuzzle, speed) => {
        const startPuzzle = getCurrentPuzzle();
        const xDiff = nextPuzzle.x - currentPuzzle.x;
        const yDiff = nextPuzzle.y - currentPuzzle.y;

        let puzzle = currentPuzzle;
        let check = 0;
        for (let i = 0; i < 100 / speed; i++) {
            if (simulatorStop()) {
                return;
            }

            if (!checkIsRunning()) {
                if ((Math.abs(nextPuzzle.x - puzzle.x) < 0.5 && Math.abs(nextPuzzle.y - puzzle.y) === 0)
                    || (Math.abs(nextPuzzle.y - puzzle.y) < 0.5 && Math.abs(nextPuzzle.x - puzzle.x) === 0)
                ) {
                    setCurrentPuzzleState(JSON.stringify(nextPuzzle));
                    return getPuzzleByPosition(nextPuzzle.x, nextPuzzle.y);
                }
                if ((Math.abs(nextPuzzle.x - puzzle.x) >= 0.5 && Math.abs(nextPuzzle.y - puzzle.y) === 0)
                    || (Math.abs(nextPuzzle.y - puzzle.y) >= 0.5 && Math.abs(nextPuzzle.x - puzzle.x) === 0)
                ) {
                    setCurrentPuzzleState(JSON.stringify(startPuzzle));
                    return getPuzzleByPosition(startPuzzle.x, startPuzzle.y);
                }
            }

            if (nextPuzzle.x !== puzzle.x) {
                let x = puzzle.x * 100;
                x += xDiff * speed;
                puzzle.x = Math.round(x * 10) / 1000;
            }
            if (nextPuzzle.y !== puzzle.y) {
                let y = puzzle.y * 100;
                y += yDiff * speed;
                puzzle.y = Math.round(y * 10) / 1000;
            }

            if (check === 0 && !checkIsRunning()) {
                changeColor(null)
                    .then(() => changeColor('white'))
                    .then(() => changeColor(null))
                    .then(() => changeColor('white'))
                    .then(() => changeColor(null));
                check = 1;
            }

            if (readStorageCommand() === 'line_jump') {
                changeColor('green')
                    .then(() => changeColor('blue'))
                    .then(() => changeColor('white'));
            }

            setCurrentPuzzleState(JSON.stringify(puzzle));
            await sleep(5);
        }

        return getPuzzleByPosition(puzzle.x, puzzle.y);
    };

    const rotateRight45 = async direction => {
        const directionsNames = Object.keys(directions);
        const index = directionsNames.indexOf(direction);
        const newDirection = index + 1 === directionsNames.length ? directionsNames[0] : directionsNames[index + 1];
        setOzobotDirection(newDirection);
        return newDirection;
    };

    const rotateRight = async direction => {
        for (let i = 0; i < 2; i++) {
            direction = await rotateRight45(direction);
            await sleep(50);
        }
        return direction;
    };

    const rotateLeft45 = async direction => {
        const directionsNames = Object.keys(directions);
        const index = directionsNames.indexOf(direction);
        const newDirection = index - 1 < 0 ? directionsNames[directionsNames.length - 1] : directionsNames[index - 1];
        setOzobotDirection(newDirection);
        return newDirection;
    };

    const rotateLeft = async direction => {
        for (let i = 0; i < 2; i++) {
            direction = await rotateLeft45(direction);
            await sleep(50);
        }
        return direction;
    };

    const rotateToDirection = async (direction, targetDirection) => {
        let newDirection = direction;
        while (targetDirection !== newDirection) {
            newDirection = await rotateRight(direction);
            direction = newDirection;
        }

        return newDirection;
    };

    const canGoToNextPuzzle = (nextPuzzle, direction) => {
        if (!nextPuzzle || ['ab', 'ag', 'al', 'ar', 'bw', 'bx', 'by', 'bz', 'ca', 'cb', 'cc', 'cd', 'ce', 'cf', 'cg',
                'ch', 'ci', 'cj', 'ck', 'cl', 'cm', 'cn', 'co', 'cp', 'cr', 'cs', 'ct', 'cu', 'cw', 'cx', 'cy', 'cz', 'da',
                'db', 'dc', 'dd', 'de', 'df', 'dg', 'dh', 'di', 'dj', 'dk', 'dl'].indexOf(nextPuzzle.id) > -1
        ) return false;
        if (['ac', 'ah', 'am', 'as', 'az', 'ba', 'bb', 'bc', 'bd', 'be', 'bf'].indexOf(nextPuzzle.id) > -1) {
            if ((['s', 'n'].indexOf(nextPuzzle.direction) > -1 && ['e', 'w'].indexOf(direction) > -1)
                || (['e', 'w'].indexOf(nextPuzzle.direction) > -1 && ['s', 'n'].indexOf(direction) > -1)
            ) {
                return false;
            }
        }
        if (['bt', 'bu', 'br', 'bp', 'bo', 'bl', 'bm', 'bn',  'bh', 'bi', 'bj', 'bk'].indexOf(nextPuzzle.id) > -1) {
            if (nextPuzzle.direction === direction) return false;
        }
        if (['ad', 'ai', 'an', 'at'].indexOf(nextPuzzle.id) > -1) {
            if ((['w'].indexOf(direction) > -1 && ['w', 'n'].indexOf(nextPuzzle.direction) > -1)
                || (['e'].indexOf(direction) > -1 && ['s', 'e'].indexOf(nextPuzzle.direction) > -1)
                || (['s'].indexOf(direction) > -1 && ['s', 'w'].indexOf(nextPuzzle.direction) > -1)
                || (['n'].indexOf(direction) > -1 && ['n', 'e'].indexOf(nextPuzzle.direction) > -1)
            ) {
                return false;
            }
        }
        if (['af', 'ak', 'ap', 'aw'].indexOf(nextPuzzle.id) > -1) {
            if ((direction === 'w' && nextPuzzle.direction === 's')
                || (direction === 'n' && nextPuzzle.direction === 'w')
                || (direction === 'e' && nextPuzzle.direction === 'n')
                || (direction === 's' && nextPuzzle.direction === 'e')
            ) {
                return false;
            }
        }
        if (['ax', 'ay', 'bg', 'bs'].indexOf(nextPuzzle.id) > -1) {
            if ((nextPuzzle.direction === 'n' && ['e', 'n', 's'].indexOf(direction) > -1)
                || (nextPuzzle.direction === 's' && ['w', 'n', 's'].indexOf(direction) > -1)
                || (nextPuzzle.direction === 'w' && ['w', 'e', 'n'].indexOf(direction) > -1)
                || (nextPuzzle.direction === 'e' && ['w', 's', 'e'].indexOf(direction) > -1)
            ) {
                return false;
            }
        }

        return true;
    };

    const readStorageCommand = () => {
        const storage = GetStorage();
        return storage.command;
    };

    const move = async (currentPuzzle, {direction, speed}) => {
        const nextPuzzle = findNextPuzzle(currentPuzzle, direction);
        if (readStorageCommand() !== 'line_jump') {
            if (!canGoToNextPuzzle(nextPuzzle, direction)) {
                await moveMargin(true);
                localStorage.setItem('is_ozobot_running', '0');
                return null;
            }
        }

        if (simulatorStop()) {
            return;
        }

        if (nextPuzzle === null) {
            await moveMargin(true);
            return null;
        }

        const target = await moveToTarget(currentPuzzle, nextPuzzle, speed);
        setCurrentPuzzle(target);

        return target;
    };

    const simulatorStop = () => {
        if (localStorage.getItem('simulator') === 'stop') {
            const startPuzzle = {
                x: -1,
                y: -1,
                direction: 'n',
                id: 'ozobot',
            };
            setCurrentPuzzle(startPuzzle);
            setCurrentPuzzleState(JSON.stringify(startPuzzle));

            return true;
        }

        return false;
    };

    const changeColor = async (color, time = 100) => {
        if (simulatorStop()) {
            return;
        }
        setOzobotColor(color);
        await sleep(time);
    };

    const moveMarginLeft = async (speed) => {
        for (let i = 0; i >= -20; i -= 2 * speed) {
            setOzobotLeftRightMargin(i);
            await sleep(10);
        }
        setOzobotLeftRightMargin(-20);

        for (let i = -20; i <= 0; i += 2 * speed) {
            setOzobotLeftRightMargin(i);
            await sleep(10);
        }
        setOzobotLeftRightMargin(0);
    };

    const moveMarginRight = async (speed) => {
        for (let i = 0; i <= 20; i += 2 * speed) {
            setOzobotLeftRightMargin(i);
            await sleep(10);
        }
        setOzobotLeftRightMargin(20);

        for (let i = 20; i >= 0; i -= 2 * speed) {
            setOzobotLeftRightMargin(i);
            await sleep(10);
        }
        setOzobotLeftRightMargin(0);
    };

    const zigzag = async (ozobotSpeed, backwards = false) => {
        setOzobotColor(backwards ? 'purple' : 'green');
        return moveMarginLeft(ozobotSpeed)
            .then(() => {
                setOzobotColor(backwards ? 'purple' : 'green');
                return moveMarginRight(ozobotSpeed);
            })
            .then(() => {
                setOzobotColor(backwards ? 'purple' : 'green');
                return moveMarginLeft(ozobotSpeed);
            });
    };

    const moveMargin = async (withColor = false) => {
        for (let i = 0; i <= 30; i += 2) {
            setOzobotMargin(i);
            await sleep(10);
        }

        if (withColor) {
            changeColor(null)
                .then(() => changeColor('white'))
                .then(() => changeColor(null))
                .then(() => changeColor('white'));
        }

        for (let i = 30; i >= 0; i -= 2) {
            setOzobotMargin(i);
            await sleep(10);
        }
    };

    const checkIsRunning = () => localStorage.getItem('is_ozobot_running') === '1';

    const findPath = async currentPuzzle => {
        let params = {
            direction: ozobotDirection,
            speed: 1,
            color: ozobotColor,
        };

        localStorage.setItem('is_first_puzzle', '1');

        while (currentPuzzle !== null) {
            if (!checkIsRunning()) break;
            if (simulatorStop()) {
                return;
            }
            await RunPuzzleCommand(currentPuzzle, params, rotateLeft, rotateRight, sleep, rotateLeft45, rotateRight45,
                changeColor, moveMargin, zigzag, rotateToDirection);

            currentPuzzle = await move(currentPuzzle, params);
            if (simulatorStop()) {
                return;
            }

            if (currentPuzzle !== null && ['ab', 'ag', 'al', 'ar', 'bw', 'bx', 'by', 'bz', 'ca', 'cb', 'cc', 'cd', 'ce',
                'cf', 'cg', 'ch', 'ci', 'cj', 'ck', 'cl', 'cm', 'cn', 'co', 'cp', 'cr', 'cs', 'ct', 'cu', 'cw', 'cx',
                'cy', 'cz', 'da', 'db', 'dc', 'dd', 'de', 'df', 'dg', 'dh', 'di', 'dj', 'dk', 'dl'].indexOf(currentPuzzle.id) === -1
            ) {
                if (readStorageCommand() === 'line_jump') {
                    ClearOzobotStorage();
                }
            }
            localStorage.setItem('is_first_puzzle', '0');
        }
        localStorage.setItem('is_ozobot_running', '0');
        setIsOzobotStopped(true);
    };

    const findStartPuzzle = () => {
        const puzzles = JSON.parse(localStorage.getItem('puzzlesState'));
        const filtered = puzzles.puzzlesOnBoard.present.filter(p => p.id === 'ax');

        return filtered.length > 0 ? filtered[0] : null;
    };

    const findRandomPuzzle = () => {
        const puzzles = JSON.parse(localStorage.getItem('puzzlesState'));
        const present = puzzles.puzzlesOnBoard.present;

        return present[Math.floor(Math.random() * present.length)];
    };

    const checkArePuzzles = () => {
        const puzzles = JSON.parse(localStorage.getItem('puzzlesState'));
        return puzzles.puzzlesOnBoard.present.length > 0;
    };

    const handleSimulatorStart = () => {
        if (!checkArePuzzles()) return;

        setIsSimulatorStarted(true);
        localStorage.setItem('simulator', 'start');
        let startPuzzle = findStartPuzzle();
        if (startPuzzle === null) {
            startPuzzle = findRandomPuzzle();
        }

        setOzobotDirection(startPuzzle.direction);

        setCurrentPuzzle(startPuzzle);
        setCurrentPuzzleState(JSON.stringify(startPuzzle));
        setOzobotColor('white');
    };

    const handleSimulatorEnd = async () => {
        setIsSimulatorStarted(false);
        handleStop();
        localStorage.setItem('simulator', 'stop');
        simulatorStop();
    };

    const handleOnStart = async () => {
        localStorage.setItem('is_ozobot_running', '1');
        localStorage.removeItem('actual_color');
        localStorage.removeItem('ozobot_stoper_start');
        localStorage.removeItem('counter_command');
        localStorage.removeItem('counter');

        setIsOzobotStopped(false);

        ClearOzobotStorage();
        setOzobotColor('white');

        let puzzle = getCurrentPuzzle();
        if (puzzle === null) {
            localStorage.setItem('is_ozobot_running', '0');
            setIsOzobotStopped(true);
            return;
        }
        puzzle = getPuzzleByPosition(puzzle.x, puzzle.y);
        if (puzzle === null) {
            localStorage.setItem('is_ozobot_running', '0');
            setIsOzobotStopped(true);
            return;
        }

        await findPath(puzzle);
    };

    const handleStop = () => {
        localStorage.setItem('is_ozobot_running', '0');
        localStorage.removeItem('actual_color');
        localStorage.removeItem('ozobot_stoper_start');
        localStorage.removeItem('counter_command');
        localStorage.removeItem('counter');
    };

    return <WorkingSpace
        {...props}
        isOzobotStopped={isOzobotStopped}
        ozobotPosition={JSON.parse(currentPuzzleState)}
        onSimulatorStart={handleSimulatorStart}
        onSimulatorEnd={handleSimulatorEnd}
        canStartSimulator={checkArePuzzles}
        isSimulatorStarted={isSimulatorStarted}
        onStart={handleOnStart}
        onStop={handleStop}
        onRotateRight={() => rotateRight(ozobotDirection)}
        onRotateLeft={() => rotateLeft(ozobotDirection)}
        renderOzobot={renderOzobot}
        onPuzzleOver={onOzobotOverPuzzleOver}
        puzzleSize={puzzleSize}
        scale={scale}
        onScaleChanged={handleScaleChange}
        diffCounter={diffCounter}
        onDiffCounterChanged={d => setDiffCounter(d)}
        onDrawingDragOver={handleDrawingDragOver}
        dragX={clientDragX}
        dragY={clientDragY}
        canvas={canvas}
        canvasContainer={canvasContainer}
        getCanvas={getCanvas}
        onCanvasChanged={c => setCanvas(c)}
        directions={directions}
    />;
}



// WEBPACK FOOTER //
// ./src/Simulator/Simulator.js